import { Component, Emit, Prop, Vue, Watch } from "vue-property-decorator";
import DriveItem from "@/components/DriveItem.vue";
import Loading from "@/components/Loading.vue";
import UploadButton from "@/components/UploadButton.vue";
import { DateUtils } from "@/utils/dateUtils";
import { Consts } from '@/consts';
import { GraphApiResult } from '@/models/graphApiResult';
import { DriveItem as mDriveItem } from '@/models/driveItem';
import { AppConfig } from '@/app-config';
import { GroupUtils } from '@/utils/groupUtils';
import { ApiResult } from '@/enums';
import { FileUtils } from '@/utils/fileUtils';
import { DriveItems } from '@/models/driveItems';
import { GraphApiUtils } from '@/utils/graphApiUtils';
import { UploadSession } from '@/models/uploadSession';

@Component({
	components: {
		DriveItem,
		Loading,
		UploadButton
	}
})

export default class EditBucket extends Vue {
	@Prop() public bucketId!: string;
	@Prop() public title!: string;
	@Prop() public name!: string;
	@Prop() public dateTitle!: string;
	@Prop() public date!: string;
	@Prop() public channelId!: string;
	@Prop() public uploadPath!: string;
	private groupId: string = '';
	private inputDate: string = '';
	private driveItems: DriveItems = new DriveItems();
	private nameTitle: string = "タイトル";
	private namePlaceholder: string = "タイトルを入力してください";
	private filesTitle: string = "ファイル一覧";
	private datePlaceholder: string = "日付を入力してください";
	private driveItemsLoading: boolean = false;
	private uploadButtonDisabled: boolean = false;
	private uploadButtonUploading: boolean = false;
	private message: string = 'ファイルがありません';
	private isVisibleMessage: boolean = false;
	public async created(): Promise<void> {
		// アクセストークンが有効でない場合はルートに遷移。
		if (!await GraphApiUtils.isValidAccessToken(Vue.$accessTokenInfo)) {
			this.$router.push(Consts.PATH_USER);
			return;
		}
		const accessToken: string = Vue.$accessTokenInfo.token;
		// TODO DriveItems.vueをコンポーネント化して流用できると思う
		const groupId: string | null = localStorage.getItem(Consts.LOCAL_STORAGE_KEY_USER_GROUPID);
		if (groupId === null) {
			return;
		}
		this.groupId = groupId;
		this.inputDate = this.date;
		await this.updateDriveItems(accessToken, this.groupId, this.uploadPath);
	}

	@Emit("close")
	public close(bucketId: string, inputDate: string): void { }


	@Emit("clear-date")
	public clearDate(): void { }

	private async updateDriveItems(accessToken: string, groupId: string, path: string): Promise<void> {
		try {
			this.driveItemsLoading = true;
			this.driveItems = new DriveItems();
			const driveItemsResult = await GroupUtils.GetDriveItemsByPath(accessToken, groupId, path);
			if ((driveItemsResult.result !== ApiResult.Success) || (driveItemsResult.value === null)) {
				alert('ファイル一覧の取得に失敗しました');
				return;
			}
			this.driveItems = driveItemsResult.value;
			this.isVisibleMessage = (this.driveItems.value.length > 0) ? false : true;
		} finally {
			this.driveItemsLoading = false;
		}
	}

	private async postDriveItem(accessToken: string, groupId: string, path: string, file: File): Promise<void> {
		try {
			this.uploadButtonUploading = true;
			this.uploadButtonDisabled = true;

			// アップロード先のDriveItem一覧を取得する
			const driveItemsResult = await GroupUtils.GetDriveItemsByPath(accessToken, groupId, path);
			if ((driveItemsResult.result !== ApiResult.Success) || (driveItemsResult.value === null)) {
				alert('ファイル一覧の取得に失敗しました');
				return;
			}

			const driveItems = driveItemsResult.value;
			// ファイル名が重複しないようにする
			const increamentName: string = driveItems.increamentName(file.name);
			// ファイルをアップロードする
			if (file.size < Consts.GRAPH_API_UPLOAD_MAX_FILESIZE) {
				// 4MBより小さい場合
				const driveItemResult = await GroupUtils.PostDriveItemByPath(accessToken, groupId, path, increamentName, file);
				if ((driveItemResult.result !== ApiResult.Success) || (driveItemResult.value === null)) {
					return;
				}
			} else {
				const filePath: string = `${path}/${increamentName}`;
				const uploadSessionResult = await GroupUtils.PostUploadSession(accessToken, groupId, filePath);
				if ((uploadSessionResult.result !== ApiResult.Success) || (uploadSessionResult.value === null)) {
					return;
				}
				const uploadSession: UploadSession = uploadSessionResult.value;
				const driveItemResult = await GroupUtils.UploadFile(uploadSession.uploadUrl, file);
				if ((driveItemResult.result !== ApiResult.Success) || (driveItemResult.value === null)) {
					return;
				}
				// TODO エラー時にセッションを削除する（一定時間で消えるので気にしなくていいかも）
			}
		} finally {
			this.uploadButtonUploading = false;
			this.uploadButtonDisabled = false;
		}
	}

	private onDateChange(e: Event): void {
		const input: HTMLInputElement = e.target as HTMLInputElement;
		this.inputDate = input.value;
	}

	private onClearDateClick(): void {
		this.inputDate = '';
		this.clearDate();
	}

	private onCloseClick(): void {
		if (this.inputDate === null) {
			this.inputDate = '';
		}
		this.close(this.bucketId, this.inputDate);
	}

	private async onUploadButtonInputChange(files: FileList, uploadPath: string): Promise<void> {
		const file: File | null = files.item(0);
		if ((file === null) || (uploadPath === undefined)) {
			return;
		}
		const accessToken: string = Vue.$accessTokenInfo.token;
		// ファイルをアップロードする
		await this.postDriveItem(accessToken, this.groupId, uploadPath, file);
		// ファイルリストを更新する
		await this.updateDriveItems(accessToken, this.groupId, uploadPath);
	}

	private async onDeleteBucketItem(driveItemId: string): Promise<void> {
		const accessToken: string = Vue.$accessTokenInfo.token;
		const deleteResult: GraphApiResult<void> = await GroupUtils.DelteDriveItem(accessToken, this.groupId, driveItemId);
		if (deleteResult.result !== ApiResult.Success) {
			alert('ファイルの削除に失敗しました');
			return;
		}
		await this.updateDriveItems(accessToken, this.groupId, this.uploadPath);
	}
}
